import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { UserPlus, RotateCcw, Save, XCircle } from 'lucide-react';
import { ADMIN } from '@/constants/testIds';

const WEIGHT_KEYS = [
  ['demographic', 'Demográfico'],
  ['demand', 'Demanda'],
  ['commercial_intensity', 'Intensidad comercial'],
  ['competition', 'Competencia'],
  ['complementarity', 'Complementariedad'],
  ['accessibility', 'Accesibilidad'],
  ['dynamism', 'Dinamismo'],
  ['tourism', 'Turismo'],
  ['financial', 'Financiero'],
  ['information_quality', 'Calidad de info'],
];

export default function AdminPanel() {
  return (
    <div className="p-8 md:p-12 max-w-[1400px]">
      <div className="label-caps">Administración</div>
      <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-6">Panel administrativo</h1>

      <Tabs defaultValue="users">
        <TabsList className="mb-6 bg-white border border-slate-200 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid={ADMIN.tabUsers}>Usuarios</TabsTrigger>
          <TabsTrigger value="weights" data-testid={ADMIN.tabWeights}>Ponderaciones</TabsTrigger>
          <TabsTrigger value="categories" data-testid="admin-tab-categories">Categorías</TabsTrigger>
          <TabsTrigger value="regions" data-testid="admin-tab-regions">Regiones</TabsTrigger>
          <TabsTrigger value="sources" data-testid={ADMIN.tabSources}>Fuentes</TabsTrigger>
          <TabsTrigger value="google" data-testid="admin-tab-google">Gasto Google</TabsTrigger>
          <TabsTrigger value="usage" data-testid={ADMIN.tabUsage}>Consumo</TabsTrigger>
        </TabsList>

        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="weights"><WeightsTab /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="regions"><RegionsTab /></TabsContent>
        <TabsContent value="sources"><SourcesTab /></TabsContent>
        <TabsContent value="google"><GoogleUsageTab /></TabsContent>
        <TabsContent value="usage"><UsageTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'analyst', organization: '' });

  const load = () => api.get('/admin/users').then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post('/admin/users', form);
      toast.success('Usuario creado');
      setOpen(false); setForm({ name: '', email: '', password: '', role: 'analyst', organization: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  const toggleActive = async (u) => {
    if (u.active) await api.delete(`/admin/users/${u.id}`);
    else await api.patch(`/admin/users/${u.id}`, { active: true });
    load();
  };

  return (
    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between">
        <div className="font-semibold">Usuarios ({users.length})</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={ADMIN.createUserBtn} className="bg-blue-900 hover:bg-blue-800"><UserPlus size={14} className="mr-2" /> Crear usuario</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nuevo usuario</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Correo</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Contraseña</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
              <div><Label>Empresa</Label><Input value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1" /></div>
              <div><Label>Rol</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="analyst">Analista</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={create} className="w-full bg-blue-900 hover:bg-blue-800 mt-4">Crear</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-3 label-caps">Nombre</th><th className="p-3 label-caps">Email</th><th className="p-3 label-caps">Rol</th><th className="p-3 label-caps">Estado</th><th className="p-3 label-caps">Acciones</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="p-3 font-medium">{u.name}</td>
              <td className="p-3 text-slate-700">{u.email}</td>
              <td className="p-3"><span className="text-xs uppercase tracking-widest font-semibold px-2 py-0.5 bg-slate-200">{u.role}</span></td>
              <td className="p-3">{u.active ? <span className="text-green-800 text-xs font-semibold uppercase">Activo</span> : <span className="text-slate-500 text-xs font-semibold uppercase">Inactivo</span>}</td>
              <td className="p-3">
                <button onClick={() => toggleActive(u)} className="text-xs text-red-700 hover:underline flex items-center gap-1"><XCircle size={12} /> {u.active ? 'Desactivar' : 'Activar'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const WeightsTab = () => {
  const [weights, setWeights] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get('/admin/weights').then((r) => setWeights(r.data)); }, []);

  const total = WEIGHT_KEYS.reduce((s, [k]) => s + ((weights[k] || 0)), 0);

  const set = (k, v) => setWeights({ ...weights, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(WEIGHT_KEYS.map(([k]) => [k, Number(weights[k]) || 0]));
      await api.put('/admin/weights', payload);
      toast.success('Ponderaciones guardadas');
    } catch (e) {
      toast.error('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    const r = await api.post('/admin/weights/reset');
    setWeights(r.data);
    toast.success('Ponderaciones restauradas');
  };

  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="font-semibold">Ponderaciones del score general</div>
          <div className="text-xs text-slate-500">Total actual: {(total * 100).toFixed(0)}% (recomendado: 100%)</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={reset} data-testid={ADMIN.resetWeightsBtn}><RotateCcw size={14} className="mr-2" /> Restaurar</Button>
          <Button onClick={save} disabled={saving} data-testid={ADMIN.saveWeightsBtn} className="bg-blue-900 hover:bg-blue-800"><Save size={14} className="mr-2" /> Guardar</Button>
        </div>
      </div>
      <div className="space-y-4">
        {WEIGHT_KEYS.map(([k, l]) => (
          <div key={k}>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">{l}</div>
              <div className="text-sm font-mono">{((weights[k] || 0) * 100).toFixed(0)}%</div>
            </div>
            <Slider
              data-testid={ADMIN.weightSlider(k)}
              value={[Math.round((weights[k] || 0) * 100)]}
              onValueChange={(v) => set(k, v[0] / 100)}
              max={40} min={0} step={1}
              className="mt-1"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const SourcesTab = () => {
  const [sources, setSources] = useState([]);
  useEffect(() => { api.get('/catalog/sources').then((r) => setSources(r.data)); }, []);
  return (
    <div className="bg-white border border-slate-200 p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sources.map((s) => (
          <div key={s.key} className="border border-slate-200 p-4 bg-slate-50">
            <div className="flex items-center justify-between">
              <div className="font-semibold">{s.name}</div>
              <span className={`text-xs uppercase tracking-widest font-semibold px-2 py-0.5 ${s.status === 'disponible' ? 'bg-green-100 text-green-800' : s.status === 'no_configurado' ? 'bg-slate-200 text-slate-600' : 'bg-amber-100 text-amber-800'}`}>{s.status.replace('_', ' ')}</span>
            </div>
            <div className="text-xs text-slate-500 mt-1">{s.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const UsageTab = () => {
  const [data, setData] = useState({ logs: [], aggregated: {} });
  const [stats, setStats] = useState({});
  useEffect(() => {
    api.get('/admin/api-usage').then((r) => setData(r.data));
    api.get('/admin/reports-stats').then((r) => setStats(r.data));
  }, []);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Análisis totales" value={stats.total || 0} />
        <StatCard label="Completados" value={stats.completed || 0} />
        <StatCard label="Fallidos" value={stats.failed || 0} />
        <StatCard label="PDFs generados" value={stats.pdf_generated || 0} />
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <div className="label-caps mb-3">Consumo por fuente</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-2">Fuente</th><th className="p-2">Consultas</th><th className="p-2">Errores</th><th className="p-2">Costo estimado</th></tr></thead>
          <tbody>
            {Object.entries(data.aggregated).map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100"><td className="p-2">{k}</td><td className="p-2">{v.count}</td><td className="p-2 text-red-700">{v.errors}</td><td className="p-2">${v.cost.toFixed(3)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-slate-200 p-6">
        <div className="label-caps mb-3">Últimas consultas</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-2">Timestamp</th><th className="p-2">Fuente</th><th className="p-2">Estado</th><th className="p-2">Error</th></tr></thead>
          <tbody>
            {data.logs.slice(0, 30).map((l) => (
              <tr key={l.id} className="border-b border-slate-100 text-xs">
                <td className="p-2">{l.timestamp?.slice(0, 19).replace('T', ' ')}</td>
                <td className="p-2">{l.source}</td>
                <td className="p-2">{l.status}</td>
                <td className="p-2 text-red-700">{l.error}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatCard = ({ label, value }) => (
  <div className="bg-white border border-slate-200 p-4">
    <div className="label-caps">{label}</div>
    <div className="font-display text-3xl font-extrabold mt-1 text-blue-900">{value}</div>
  </div>
);

const RegionsTab = () => {
  const [items, setItems] = useState([]);
  const [states, setStates] = useState([]);
  const [munis, setMunis] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ id: '', name: '', description: '', municipalities: [] });

  const load = () => api.get('/catalog/regions').then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/catalog/states').then((r) => setStates(r.data));
  }, []);
  useEffect(() => {
    if (selectedState) api.get(`/catalog/municipalities/${selectedState}`).then((r) => setMunis(r.data));
  }, [selectedState]);

  const openCreate = () => { setEditing(null); setForm({ id: '', name: '', description: '', municipalities: [] }); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ id: r.id, name: r.name, description: r.description || '', municipalities: r.municipalities || [] });
    setOpen(true);
  };

  const toggleMuni = (m) => {
    const key = `${selectedState}-${m.code}`;
    const exists = form.municipalities.find((x) => `${x.state_code}-${x.muni_code}` === key);
    if (exists) {
      setForm({ ...form, municipalities: form.municipalities.filter((x) => `${x.state_code}-${x.muni_code}` !== key) });
    } else {
      setForm({ ...form, municipalities: [...form.municipalities, { state_code: selectedState, muni_code: m.code, name: m.name }] });
    }
  };

  const save = async () => {
    if (!form.name.trim() || form.municipalities.length === 0) {
      toast.error('Nombre y al menos 1 municipio requeridos'); return;
    }
    try {
      await api.post('/admin/regions', form);
      toast.success('Región guardada');
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (r) => {
    if (!window.confirm(`¿Eliminar región "${r.name}"?`)) return;
    await api.delete(`/admin/regions/${r.id}`);
    load();
  };

  return (
    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-semibold">Regiones curadas ({items.length})</div>
          <div className="text-xs text-slate-500">Regiones o corredores que agrupan múltiples municipios de uno o más estados.</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={openCreate} data-testid="admin-create-region" className="bg-blue-900 hover:bg-blue-800"><UserPlus size={14} className="mr-2" /> Nueva región</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nueva'} región</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" placeholder="Ej. Corredor Bajío" data-testid="admin-region-name" /></div>
              <div><Label>Identificador (opcional)</Label><Input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} className="mt-1" placeholder="corredor_bajio" /></div>
              <div className="md:col-span-2"><Label>Descripción</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
              <div className="md:col-span-2"><Label>Agregar municipios (busca por estado)</Label>
                <Select value={selectedState} onValueChange={setSelectedState}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder="Selecciona estado" /></SelectTrigger>
                  <SelectContent>{states.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {selectedState && (
                <div className="md:col-span-2 max-h-64 overflow-y-auto border border-slate-200 p-2">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                    {munis.slice(0, 200).map((m) => {
                      const key = `${selectedState}-${m.code}`;
                      const checked = !!form.municipalities.find((x) => `${x.state_code}-${x.muni_code}` === key);
                      return (
                        <label key={m.code} className={`flex items-center gap-2 p-2 border cursor-pointer text-xs ${checked ? 'border-blue-900 bg-blue-50' : 'border-slate-200'}`}>
                          <Checkbox checked={checked} onCheckedChange={() => toggleMuni(m)} />
                          {m.name}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <div className="label-caps mb-2">Municipios en la región ({form.municipalities.length})</div>
                <div className="flex flex-wrap gap-1">
                  {form.municipalities.map((m, i) => (
                    <span key={i} className="text-xs px-2 py-1 bg-slate-100 border border-slate-200">{m.name}
                      <button onClick={() => setForm({ ...form, municipalities: form.municipalities.filter((x, j) => j !== i) })} className="ml-2 text-red-700">×</button>
                    </span>
                  ))}
                </div>
              </div>
              <Button onClick={save} className="md:col-span-2 bg-blue-900 hover:bg-blue-800 mt-2" data-testid="admin-region-save">Guardar región</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-3 label-caps">Nombre</th><th className="p-3 label-caps">Descripción</th><th className="p-3 label-caps">Municipios</th><th className="p-3 label-caps">Acciones</th></tr></thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} data-testid={`region-row-${r.id}`} className="border-b border-slate-100">
              <td className="p-3 font-medium">{r.name}</td>
              <td className="p-3 text-slate-600 text-xs">{r.description || '—'}</td>
              <td className="p-3 text-slate-600">{(r.municipalities || []).length}</td>
              <td className="p-3">
                <button onClick={() => openEdit(r)} className="text-xs text-blue-900 hover:underline mr-3">Editar</button>
                <button onClick={() => del(r)} className="text-xs text-red-700 hover:underline">Eliminar</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Sin regiones — crea la primera.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

const GoogleUsageTab = () => {
  const [data, setData] = useState({ by_source: {}, total_cost_usd: 0, last_calls: [] });
  useEffect(() => { api.get('/admin/google-usage').then((r) => setData(r.data)); }, []);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Costo total USD" value={`$${data.total_cost_usd.toFixed(3)}`} />
        <StatCard label="Google Places" value={data.by_source.google_places?.count || 0} />
        <StatCard label="Google Maps (Geo+Static)" value={data.by_source.google_maps?.count || 0} />
        <StatCard label="Tavily búsquedas" value={data.by_source.tavily?.count || 0} />
      </div>
      <div className="bg-white border border-slate-200 p-6">
        <div className="label-caps mb-3">Desglose por servicio</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-2">Servicio</th><th className="p-2">Llamadas</th><th className="p-2">Errores</th><th className="p-2">Costo estimado</th></tr></thead>
          <tbody>
            {Object.entries(data.by_source).map(([k, v]) => (
              <tr key={k} className="border-b border-slate-100"><td className="p-2">{k}</td><td className="p-2 font-semibold">{v.count}</td><td className="p-2 text-red-700">{v.errors}</td><td className="p-2">${v.cost.toFixed(3)}</td></tr>
            ))}
            {Object.keys(data.by_source).length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Sin consumo registrado aún.</td></tr>}
          </tbody>
        </table>
        <div className="text-xs text-slate-500 mt-3">
          Referencia: Places Text Search ~$0.017 · Static Maps ~$0.002 · Geocoding fallback ~$0.005 · Tavily ~$0.005/búsqueda.
        </div>
      </div>
      <div className="bg-white border border-slate-200 p-6">
        <div className="label-caps mb-3">Últimas llamadas</div>
        <table className="w-full text-xs">
          <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-2">Timestamp</th><th className="p-2">Servicio</th><th className="p-2">Estado</th><th className="p-2">Costo</th></tr></thead>
          <tbody>
            {(data.last_calls || []).slice(0, 40).map((l) => (
              <tr key={l.id} className="border-b border-slate-100"><td className="p-2">{l.timestamp?.slice(0, 19).replace('T', ' ')}</td><td className="p-2">{l.source}</td><td className="p-2">{l.status}</td><td className="p-2">${(l.cost_estimated || 0).toFixed(3)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const CATEGORY_TYPES = [
  { v: 'giro', l: 'Giro comercial' },
  { v: 'competencia', l: 'Competencia' },
  { v: 'complementario', l: 'Complementario' },
  { v: 'generador_trafico', l: 'Generador de tráfico' },
];

const CategoriesTab = () => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', type: 'giro', keywords: '', parent: '' });
  const [editing, setEditing] = useState(null);

  const load = () => api.get('/categories').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', type: 'giro', keywords: '', parent: '' }); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type, keywords: (c.keywords || []).join(', '), parent: c.parent || '' }); setOpen(true); };

  const save = async () => {
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        parent: form.parent || null,
        keywords: form.keywords ? form.keywords.split(',').map((s) => s.trim()).filter(Boolean) : [],
      };
      if (editing) await api.patch(`/admin/categories/${editing.id}`, payload);
      else await api.post('/admin/categories', payload);
      toast.success('Categoría guardada');
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const del = async (c) => {
    if (!window.confirm(`¿Eliminar categoría "${c.name}"?`)) return;
    await api.delete(`/admin/categories/${c.id}`);
    load();
  };

  const filtered = filter === 'all' ? items : items.filter((c) => c.type === filter);

  return (
    <div className="bg-white border border-slate-200">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="font-semibold">Categorías ({items.length})</div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {CATEGORY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} data-testid="admin-create-category" className="bg-blue-900 hover:bg-blue-800"><UserPlus size={14} className="mr-2" /> Nueva categoría</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>{editing ? 'Editar' : 'Nueva'} categoría</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nombre</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" data-testid="admin-cat-name" /></div>
                <div><Label>Tipo</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger className="mt-1" data-testid="admin-cat-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORY_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Palabras clave (coma)</Label><Input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })} placeholder="perfume, fragancia, cosmético" className="mt-1" data-testid="admin-cat-keywords" /></div>
                <div><Label>Categoría padre (opcional)</Label><Input value={form.parent} onChange={(e) => setForm({ ...form, parent: e.target.value })} className="mt-1" /></div>
                <Button onClick={save} className="w-full bg-blue-900 hover:bg-blue-800 mt-2" data-testid="admin-cat-save">Guardar</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 text-left border-b border-slate-200"><th className="p-3 label-caps">Nombre</th><th className="p-3 label-caps">Tipo</th><th className="p-3 label-caps">Palabras clave</th><th className="p-3 label-caps">Acciones</th></tr></thead>
        <tbody>
          {filtered.map((c) => (
            <tr key={c.id} data-testid={`category-row-${c.id}`} className="border-b border-slate-100 hover:bg-slate-50">
              <td className="p-3 font-medium">{c.name}</td>
              <td className="p-3"><span className="text-xs uppercase tracking-widest font-semibold px-2 py-0.5 bg-slate-200">{c.type}</span></td>
              <td className="p-3 text-slate-600 text-xs">{(c.keywords || []).join(', ') || '—'}</td>
              <td className="p-3">
                <button onClick={() => openEdit(c)} className="text-xs text-blue-900 hover:underline mr-3">Editar</button>
                <button onClick={() => del(c)} className="text-xs text-red-700 hover:underline">Eliminar</button>
              </td>
            </tr>
          ))}
          {filtered.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500">Sin categorías en este filtro.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};
